/**
 * DriveZone — usePlacesAutocomplete hook
 *
 * Reusable hook that wires a plain <input> element to the Google Places
 * Autocomplete API, restricted to US cities only.
 *
 * Usage:
 *   const { inputRef, value, setValue, predictions, selectPrediction } =
 *     usePlacesAutocomplete({ onSelect: (description) => setOrigin(description) })
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import api from '@/lib/api'

interface Prediction {
  description: string
  place_id: string
}

interface UsePlacesOptions {
  onSelect: (description: string) => void
}

export function usePlacesAutocomplete({ onSelect }: UsePlacesOptions) {
  const [value, setValue]           = useState('')
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [sessionToken]              = useState(() => uuidv4())
  const inputRef                    = useRef<HTMLInputElement>(null)
  const debounceRef                 = useRef<ReturnType<typeof setTimeout>>()

  const fetchPredictions = useCallback(async (q: string) => {
    if (q.length < 2) { setPredictions([]); return }
    try {
      const res = await api.get<{ predictions: Prediction[] }>(
        `/api/search/autocomplete/?q=${encodeURIComponent(q)}&session_token=${sessionToken}`
      )
      setPredictions(res.data.predictions ?? [])
      setShowDropdown(true)
    } catch {
      setPredictions([])
    }
  }, [sessionToken])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value
    setValue(v)
    clearTimeout(debounceRef.current)
    if (v.length < 2) { setPredictions([]); setShowDropdown(false); return }
    // Debounce 250ms so we don't fire on every keystroke
    debounceRef.current = setTimeout(() => fetchPredictions(v), 250)
  }

  function selectPrediction(pred: Prediction) {
    setValue(pred.description)
    setPredictions([])
    setShowDropdown(false)
    onSelect(pred.description)
    inputRef.current?.blur()
  }

  function handleBlur() {
    // Small delay so click on a prediction fires before hiding
    setTimeout(() => setShowDropdown(false), 150)
  }

  useEffect(() => () => clearTimeout(debounceRef.current), [])

  return { inputRef, value, setValue, predictions, showDropdown, handleChange, handleBlur, selectPrediction }
}
